import { faUser } from '@fortawesome/pro-light-svg-icons';
import { faFloppyDisk, faPencil } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Input, Label } from '@playcastdotio/Shadcn';
import { useContext, useEffect, useState } from 'react';
import { PlaycastContext } from '../PlaycastWrapper';
import clsx from 'clsx';

type LocalProps = {
  displayName: string;
  remoteId: string;
  active: boolean;
  playerCount: number;
};

export const Station = ({ displayName, remoteId, active, playerCount }: LocalProps) => {
  const context = useContext(PlaycastContext);
  const [isEditing, setIsEditing] = useState(false);
  const [currentName, setCurrentName] = useState(displayName);
  const [selectedHost, setSelectedHost] = useState(context.stateStores.remoteHostList.selectedHostId);

  const handleEditToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(!isEditing);
    // setIsEditing((prev) => !prev);
  };

  useEffect(() => {
    if (!active) return;

    if (!isEditing) {
      // Always reset to original name if empty
      if (!currentName.trim()) {
        setCurrentName(displayName);
        setIsEditing(false);
        return;
      }

      // Only save if name is different and not empty
      if (currentName.trim() !== displayName) {
        console.log('Saving new name:', currentName);
        context.targetFunctions?.renameHost(currentName, remoteId);
      }
    }
  }, [isEditing, remoteId, currentName]);

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentName(e.target.value);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Check if the related target is the save button
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget?.closest('[data-testid="edit-button"]')) {
      return;
    }

    setCurrentName(displayName);
    setIsEditing(false);
  };

  const handleItemClicked = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isEditing && active) {
      // Only handle click if not editing
      console.log('Item clicked, setting host:', remoteId);
      setSelectedHost(remoteId);
      context.changeFunctionTarget(remoteId);
    }
  };

  useEffect(() => {
    setSelectedHost(context.stateStores.remoteHostList.selectedHostId);
  }, [context.stateStores.remoteHostList.selectedHostId]);

  useEffect(() => {
    setCurrentName(displayName);
  }, [displayName]);

  return (
    <div
      className={clsx(
        'flex w-full items-center gap-1 rounded-lg px-1 py-2 text-white',
        active ? 'cursor-pointer bg-gray-600' : 'cursor-not-allowed opacity-50',
      )}
      onClick={handleItemClicked}
      data-testid='station-container'
    >
      <button
        onClick={handleEditToggle}
        onBlur={(e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('Button blur');
        }}
        className='flex items-center disabled:cursor-not-allowed'
        data-testid='edit-button'
        disabled={!active}
      >
        <FontAwesomeIcon
          icon={isEditing ? faFloppyDisk : faPencil}
          className='h-3 w-3 rounded-md bg-gray-400 p-1 text-black'
          data-testid={isEditing ? 'save-icon' : 'edit-icon'}
        />
      </button>
      {isEditing ? (
        <Input
          value={currentName}
          className='m-0 h-auto w-full border-0 p-0 px-1 text-xs'
          onChange={handleValueChange}
          onBlur={handleBlur}
          data-testid='station-name-input'
          autoFocus
        />
      ) : (
        <Label
          className={clsx('text-xs truncate', selectedHost === remoteId ? 'text-amber-400' : '', active ? 'cursor-pointer' : 'cursor-not-allowed')}
          data-testid='station-name-label'
        >
          {currentName}
        </Label>
      )}
      <div className='ml-auto flex items-center gap-1 rounded-full bg-amber-400 px-2 text-black'>
        <FontAwesomeIcon icon={faUser} className='h-3 w-3' />
        <span data-testid='player-count'>{playerCount}</span>
      </div>
    </div>
  );
};
